import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Modal, Button, Form } from "react-bootstrap";
import {
  faPaperPlane,
  faComments,
  faChevronUp,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  CalendarSvgIcon,
  FilterSvgIcon,
  GearSvgIcon,
  NoPhoneSvgIcon,
  OnOffSvgIcon,
  PhoneSvgIcon,
  PlusSvgIcon,
  ReservationSvgIcon,
  TableSvgIcon,
  UsersSvgIcon,
  XSvgIcon,
  UserSvgIcon,
  ClockSvgIcon,
  BellSvgIcon,
  BubbleTextSvgIcon,
  ZoneSvgIcon,
  PennotebookSvgIcon,
  ExtrasSvgIcon,
  ArrowUpDownIcon,
  ArrowUpDown,
  NoPhoneTwoLinesSvgIcon,
} from "../../icons/SvgIcons";
import { DefaultDateSmall, DefaultDateSmallMins } from "../DefaultImports";

const CollapsibleChatContainer = styled.div`
  position: relative;
  width: 100%;
  margin-top: 10px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.3s ease;
`;

const ChatHeader = styled.div`
  background-color: ${(props) => (props.isNewMessage ? "#99b0ad" : "#bbbbbb")};
  color: white;
  padding: 12px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const ChatTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  gap: 8px; /* Espaço consistente entre os itens */
`;

const CollapseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 1rem;
`;

const ChatContent = styled.div`
  display: ${(props) => (props.isOpen ? "flex" : "none")};
  flex-direction: column;
  height: ${(props) => (props.isOpen ? "400px" : "0")};
  transition: height 0.3s ease;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  margin: 10px;
`;

const MessageBubble = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 80%;
  margin-bottom: 10px;
  padding: 10px;
  border-radius: 20px;
  color: #fff;
  word-break: break-word;
  overflow-wrap: break-word;

  transform: rotate(360deg); /* Flip message back */

  &.user-message {
    background-color: #ee754c;
    margin-left: auto;
    border-bottom-right-radius: 0;
  }

  &.other-message {
    background-color: #99b0ad;
    margin-right: auto;
    border-bottom-left-radius: 0;
  }

  .message-info {
    font-size: 0.8rem;
    margin-bottom: 5px;
    text-align: right;
  }
`;

const ChatHistoryContainer = styled.div`
  flex-grow: 1;
  overflow: auto;
  padding: 10px;
  margin-bottom: 10px;
  overscroll-behavior: contain;

  transform: rotate(360deg); /* Flip container */

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background-color: #f4f4f4;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #bdbdbd;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #a8a8a8;
  }
`;

const TimestampCenter = styled.div`
  text-align: center;
  font-size: 0.8rem;
  margin-bottom: 5px;
  color: #6c757d;
`;

const SenderName = styled.div`
  margin-bottom: 5px;
  text-align: ${(props) => (props.isUserMessage ? "right" : "left")};
  color: #6c757d;
  font-size: 0.9rem;
`;

const StyledTextarea = styled.textarea`
  flex-grow: 1;
  border: none;
  padding: 0.375rem 0.75rem;
  resize: none;
  &:focus {
    outline: none;
  }

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background-color: #f4f4f4;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #bdbdbd;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #a8a8a8;
  }
`;

const SendButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 0 15px;
  &:focus {
    outline: none;
  }
`;

function CollapsibleChat({
  reservation,
  isClient = false,
  isNewMessage,
  onToggle,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const socket = useRef(null);
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const chatEndRef = useRef(null);

  const restaurant_info = useSelector((state) => state.restaurantDetails);
  const { restaurants } = restaurant_info;

  const chatContainerRef = useRef(null);

  // New scroll handler
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      // Scroll to top of reversed container (visual bottom)
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (isOpen) {
      let sender = userInfo ? userInfo.name : reservation.full_name;
      const websocketBaseURL = process.env.REACT_APP_WEBSOCKET_URL;
      socket.current = new WebSocket(
        `${websocketBaseURL}chat/init/${reservation.token}/${sender}/${
          isClient ? "client" : "restaurant"
        }/`
      );

      socket.current.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.type === "chat_history") {
          const sortedMessages = data.messages.sort((a, b) => {
            const dateA = new Date(a.timestamp);
            const dateB = new Date(b.timestamp);
            return dateA - dateB;
          });
          setChatHistory(sortedMessages);
        } else {
          setChatHistory((prevHistory) => {
            const updatedHistory = [...prevHistory, data];
            return updatedHistory.sort((a, b) => {
              const dateA = new Date(a.timestamp);
              const dateB = new Date(b.timestamp);
              return dateA - dateB;
            });
          });
        }
      };

      socket.current.onopen = () => {
        console.log("WebSocket Connected");
      };

      socket.current.onclose = () => {
        console.log("WebSocket Disconnected");
      };

      return () => socket.current?.close();
    }
  }, [userInfo, isOpen]);

  const sendMessage = () => {
    if (socket.current && newMessage.trim()) {
      socket.current.send(
        JSON.stringify({
          message: newMessage,
          sender: userInfo ? userInfo.name : reservation.full_name,
          client_or_restaurant: isClient ? "client" : "restaurant",
        })
      );
      setNewMessage("");
    }
  };

  const isUserMessage = (senderName) => {
    if (userInfo && senderName === userInfo.name) {
      return true;
    } else if (!userInfo && senderName === reservation.full_name) {
      return true;
    }
    return false;
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen && typeof onToggle === "function") {
      onToggle();
    }
  };

  useEffect(() => {
    if (isOpen && chatHistory.length > 0) {
      const lastMessage = chatHistory[chatHistory.length - 1];
      const sender = userInfo ? userInfo.name : reservation.full_name;

      if (lastMessage.sender === sender) {
        // chatEndRef.current?.scrollIntoView({ behavior: "auto" });
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
    }
  }, [chatHistory, isOpen]);

  return (
    <CollapsibleChatContainer>
      <ChatHeader isNewMessage={isNewMessage} onClick={toggleChat}>
        <ChatTitle>
          {isClient ? (
            "Restaurante"
          ) : (
            <h6
              style={{
                color: "#FAF6E9",
                marginBottom: "0px",
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {/* Grupo 1 - Ícone Usuário + Nome */}
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginRight: "7px",
                }}
              >
                <UserSvgIcon
                  width={20}
                  height={20}
                  strokeColor={" #FAF6E9"}
                  style={{ marginRight: "5px" }}
                />
                {reservation.full_name.substring(0, 10) + ".."}
              </span>

              {/* Grupo 2 - Ícone Calendário + Data */}
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginRight: "7px",
                }}
              >
                <CalendarSvgIcon
                  width={20}
                  height={20}
                  strokeColor={" #FAF6E9"}
                  style={{ marginRight: "5px" }}
                />
                <DefaultDateSmall date={reservation.date} />
              </span>

              {/* Grupo 3 - Ícone Relógio + Hora */}
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginRight: "7px",
                }}
              >
                <ClockSvgIcon
                  width={20}
                  height={20}
                  strokeColor={" #FAF6E9"}
                  style={{ marginRight: "5px" }}
                />
                {reservation.time}
              </span>

              {/* Grupo 4 - Ícone Usuários + Número */}
              <span style={{ display: "flex", alignItems: "center" }}>
                <UsersSvgIcon
                  width={30}
                  height={30}
                  strokeColor={" #FAF6E9"}
                  style={{ marginRight: "5px" }}
                />
                {reservation.number_of_people}
              </span>
            </h6>
          )}
        </ChatTitle>
        <CollapseButton>
          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
        </CollapseButton>
      </ChatHeader>

      <ChatContent isOpen={isOpen}>
        <ChatHistoryContainer ref={chatContainerRef}>
          {chatHistory.map((msg, index) => (
            <React.Fragment key={index}>
              <SenderName isUserMessage={isUserMessage(msg.sender)}>
                <span className="sender">{msg.sender}</span>
              </SenderName>
              <TimestampCenter>
                <span>
                  {msg.timestamp ? (
                    <DefaultDateSmallMins date={msg.timestamp} />
                  ) : (
                    "hoje"
                  )}
                </span>
              </TimestampCenter>
              <MessageBubble
                className={
                  isUserMessage(msg.sender) ? "user-message" : "other-message"
                }
              >
                <div className="message-content">
                  {msg.message.split("\n").map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))}
                </div>
              </MessageBubble>
            </React.Fragment>
          ))}
          <div ref={chatEndRef} />
        </ChatHistoryContainer>

        <InputContainer>
          <StyledTextarea
            placeholder="Escrever mensagem..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            rows={1}
          />
          <SendButton onClick={sendMessage}>
            <FontAwesomeIcon
              size="lg"
              icon={faPaperPlane}
              style={{ color: "#9FBBC1" }}
            />
          </SendButton>
        </InputContainer>
      </ChatContent>
    </CollapsibleChatContainer>
  );
}

export default CollapsibleChat;
